import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Sparkles, ArrowRight, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Context for feature access state
const FeatureAccessContext = createContext({
  access: null,
  checkFeature: () => true,
  showUpgradePrompt: () => {},
});

export function useFeatureAccess() {
  return useContext(FeatureAccessContext);
}

export function FeatureAccessProvider({ children }) {
  const [access, setAccess] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        const res = await fetch(`${API}/subscription/feature-access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setAccess(await res.json());
      } catch {}
    };
    fetchAccess();
  }, []);

  const checkFeature = (featureName) => {
    if (!access) return true; // Allow while loading
    if (access.is_active && access.available_features?.includes(featureName)) return true;
    // Legacy/pilot hosts always have access (R32/R34)
    if (access.stripe_bypass === true) return true;
    if (access.plan === "legacy" || access.plan === "pilot" || access.plan === "legacy_pilot_free") return true;
    return false;
  };

  const showUpgradePrompt = (featureName) => {
    setBlockedFeature(featureName);
    setShowPrompt(true);
  };

  const FEATURE_LABELS = {
    analytics: "Analytics Dashboard",
    team_management: "Team Management",
    custom_branding: "Custom Branding",
    advanced_scheduling: "Advanced Scheduling",
    export_data: "Data Export",
    priority_support: "Priority Support",
  };

  return (
    <FeatureAccessContext.Provider value={{ access, checkFeature, showUpgradePrompt }}>
      {children}

      <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
        <DialogContent className="max-w-md" data-testid="upgrade-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-500" />
              Upgrade Required
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              <span className="font-semibold">{FEATURE_LABELS[blockedFeature] || blockedFeature}</span> is available on the Solo plan and above.
            </p>

            {access?.status === "expired" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="trial-expired-notice">
                <p className="text-sm text-amber-800 font-medium">Your trial has ended</p>
                <p className="text-xs text-amber-600 mt-1">Upgrade to keep your dashboard features and client data.</p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-sm">Solo Plan</span>
                <Badge className="bg-black text-white text-xs">$11/mo</Badge>
              </div>
              <div className="space-y-2">
                {["Unlimited client profiles", "Full service menu with upsells", "Analytics dashboard", "Client kanban pipeline", "Inspiration gallery", "Payment links"].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPrompt(false)}>
                Maybe Later
              </Button>
              <Button
                className="flex-1 bg-black text-white hover:bg-gray-800"
                onClick={() => { setShowPrompt(false); navigate("/pricing"); }}
                data-testid="upgrade-now-btn"
              >
                Upgrade Now
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </FeatureAccessContext.Provider>
  );
}

/**
 * Inline upgrade banner - place in any section that's gated.
 * Shows a lock icon with upgrade CTA when feature is not available.
 */
export function UpgradeBanner({ feature, children }) {
  const { checkFeature, showUpgradePrompt } = useFeatureAccess();

  if (checkFeature(feature)) return children;

  return (
    <div className="relative" data-testid={`upgrade-gate-${feature}`}>
      <div className="pointer-events-none opacity-30 blur-[1px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-lg">
        <button
          onClick={() => showUpgradePrompt(feature)}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors shadow-lg"
          data-testid={`upgrade-btn-${feature}`}
        >
          <Lock className="w-4 h-4" />
          Unlock with Solo
          <Sparkles className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
